/**
 * Parses a service's Table/List response body (always plain JSON text — only Image/Document
 * responses are byte-encoded, see ServiceLookupExecutor.CallApiAsync) into a preview-friendly
 * columns/rows shape. Mirrors the JSON-only branches of api/Generation/TableData.Parse; the
 * data-URI/spreadsheet/CSV byte paths there are unreachable for a service's Table response.
 */
export function parseTablePreviewRows(text: string): { columns: string[]; rows: string[][] } {
  let root: unknown;
  try {
    root = JSON.parse(text);
  } catch {
    return { columns: [], rows: [] };
  }

  const isRow = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

  const objects: Record<string, unknown>[] = Array.isArray(root)
    ? root.filter(isRow)
    : isRow(root) && Array.isArray(root.rows)
      ? root.rows.filter(isRow)
      : isRow(root)
        ? [root]
        : [];

  if (objects.length === 0) return { columns: [], rows: [] };

  const columns = [...new Set(objects.flatMap((row) => Object.keys(row)))];
  const rows = objects.map((row) => columns.map((column) => stringifyCell(row[column])));
  return { columns, rows };
}

function stringifyCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}
